import React, {FC, Component, MouseEvent} from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';

import i18n, { translations } from 'i18n';

import { addFavorite, removeFavorite } from 'actions/registry';
import { uninstallApp } from 'actions/gallery';

import App from 'models/app';

import Modal from './Modal';
import Icon from '../Icon';
import AppScreenshotCarousel from '../AppScreenshotCarousel/index';
import StateManagementButton from '../StateManagementButton/index';
import PremiumSubscribeButton, { PremiumSubscribeButtonType } from '../Premium/PremiumSubscribeButton';
import { RatingTransition, ConnectedRatingInput } from '../Ratings/RatingInput';
import { BasicContentBlock, SectionTitle, CustomizeSign, CustomizeButtonTextContainer, CustomizeButtonContainer, CustomizeButton } from '../Common';
import { LinkAction, ActionsContainer } from '../LinkAction/index';
import { AutomationText } from '../Automation';
import { PortalContainer } from '../Portal/common';

import closeIcon from 'icons/x-black.svg';
import premiumLabelIcon from 'icons/premium-logo-text.svg';
import EditIconWhite from '../../../icons/ic_edit_white.png';

interface ClockDetailsProps {
  show: boolean;
  clock: App;
  deviceName: string;
  favoriteClocksEnabled: boolean;
  favoriteClocksCount: number;
  maxFavoriteClocks: number;
  addLoading: boolean;
  removeLoading: boolean;
  userHasGalleryGrant: boolean;
  addFavorite: () => any;
  removeFavorite: () => any;
  setFocusedClock: (id: string | null) => any;
  isFirstClock: boolean;
}

const FULL_VH = document.documentElement.clientHeight;

const ScrollableContainer = styled.div`
  overflow-y: auto;
  height: ${FULL_VH - 50}px;
  overflow-x: hidden;
`;

const StyledActionsContainer = styled(ActionsContainer)`
  margin-bottom: 22px;
`;

const StyledIcon = styled(Icon)`
  position: absolute;
  right: 13px;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: center;
  position: relative;

  padding: 11px 13px 3px;
`;

const AppName = styled.span`
  flex: 1;
  align-self: center;
  text-align: center;
  
  font-size: 17px;
  line-height: 20px;
  letter-spacing: -0.41px;

  color: #112530;
`;

const DevName = styled.div`
  width: 100%;
  text-align: center;
  margin-bottom: 3px;

  font-size: 13px;
  line-height: 16px;
  letter-spacing: -0.32px;

  color: #8f8f8f;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 5px 0px 20px;
  align-items: center;
`;

const RatingContainer = styled.div`
  padding: 20px 0px 0px;
`;

const PremiumLabel = styled.div`
  background: url(${premiumLabelIcon}) center center / contain no-repeat;
  width: 75px;
  height: 14px;
  margin: auto;
`;

const SettingsButtonContainer = styled.div`
  margin-left: 23px;
`;

const SettingsButton: FC<any> = (props) => {
  const {
    component: {
      AppDetails: { customizeButtonText }
    }
  } = translations;

  return (
      <SettingsButtonContainer>
        <CustomizeButton onClick={() => props.app.navigateToSettings()}>
          <CustomizeButtonContainer>
            <CustomizeSign src={EditIconWhite} />
            <CustomizeButtonTextContainer>
              {customizeButtonText()}
            </CustomizeButtonTextContainer>
          </CustomizeButtonContainer>
        </CustomizeButton>
      </SettingsButtonContainer>
  );
};

const SelectButton: FC<any> = (props) => {
  const {
    page: {
      Clock: { InstallButtonState }
    }
  } = translations;

  const installTexts = Object.keys(InstallButtonState).reduce((texts: any, key) => {
    texts[key] = InstallButtonState[key]();
    return texts;
  }, {});

  return (
    <ButtonContainer>
      {props.app.isPremium && !props.userHasGalleryGrant
        ? <PremiumSubscribeButton
            trigger={`app-${props.app.id}`}
            type={PremiumSubscribeButtonType.INSTALL_BUTTON}
          />
        : <StateManagementButton texts={installTexts} app={props.app} />
      }
      {props.app.isClock && props.app.isOnDevice && props.app.hasSettings ? <SettingsButton app={props.app}></SettingsButton> : null}
    </ButtonContainer>
  );
};

class ManageFavoriteAction extends Component<any> {
  state = {
    open: false
  };
  onOpen = () =>
    this.setState({
      open: true
    });

  onClose = () =>
    this.setState({
      open: false
    });

  onAddClick = () => {
    const {
      clock,
      favoriteClocksCount,
      maxFavoriteClocks,
      addFavorite
    } = this.props;

    if (favoriteClocksCount >= maxFavoriteClocks) {
      this.onOpen();
      return;
    }

    addFavorite(clock.id);
  };

  onRemoveClick = () => {
    const { clock, removeFavorite } = this.props;

    if (clock.selected) {
      removeFavorite(clock.id);
    } else {
      this.onOpen();
    }
  };

  onButtonClick = () =>
    this.props.clock.favorite ? this.onRemoveClick() : this.onAddClick();

  textColor = () => {
    const {
      clock,
      addLoading,
      removeLoading
    } = this.props;

    const add = !clock.favorite;
    const loading = addLoading || removeLoading;
    return `#${loading ? '9b9b9b' : add ? '007a81' : 'ae3543'}`;
  };

  render() {
    const { clock, addLoading, removeLoading, removeFavorite } = this.props;

    const {
      page: {
        ClockGallery: {
          MyClocks: { details }
        }
      }
    } = translations;

    let actionId = 'save-clock-action';
    let mainButtonText = addLoading ? details.addLoading : details.addButton;
    let modalText = details.limitModal;
    let buttons: any = [];

    if (clock.selected && clock.favorite) {
      actionId = 'remove-clock-action';

      mainButtonText = removeLoading
        ? details.removeLoading
        : details.removeFavoriteButton;
    } else if (clock.favorite) {
      actionId = 'remove-clock-action';

      mainButtonText = removeLoading
        ? details.removeLoading
        : details.removeButton;
      modalText = details.removeModal;

      buttons = [
        {
          text: modalText.confirmButton(),
          highlight: true,
          id: "confirm-remove-clock",
          onClick: () => {
            removeFavorite(clock.id);
            this.onClose();
          }
        }
      ];
    }

    buttons = [
      {
        text: modalText.button(),
        onClick: this.onClose,
        id: "cancel-remove-clock"
      },
      ...buttons
    ];

    return (
      <div>
        <LinkAction
          key={actionId}
          id={actionId}
          text={mainButtonText()}
          textColor={this.textColor()}
          arrow={false}
          onClick={this.onButtonClick}
        />
        {this.state.open && (
          <Modal
            title={modalText.title()}
            content={modalText.content()}
            buttons={buttons}
            onClose={this.onClose}
          />
        )}
      </div>
    );
  }
}

const mapStateToProps = ({ gallery, registry }: { gallery: any, registry: any }) => ({
  addLoading: registry.addFavorite.loading,
  removeLoading: gallery.uninstallApp.loading || registry.removeFavorite.loading,
  userHasGalleryGrant: registry.galleryGrant.userHasGrant
});

const mapDispatchToProps = (dispatch: any, { clock }: { clock: any }) => ({
  addFavorite: () => dispatch(addFavorite(clock.id)),
  removeFavorite: () => {
    if (!clock.favorite) {
      return;
    }

    if (!clock.selected && clock.isOnDevice) {
      return dispatch(uninstallApp(clock.id));
    }

    dispatch(removeFavorite(clock.id));
  }
});

const ClockDetails: FC<ClockDetailsProps> = ({
  show,
  clock,
  deviceName,
  favoriteClocksEnabled,
  favoriteClocksCount,
  maxFavoriteClocks,
  addLoading,
  removeLoading,
  userHasGalleryGrant,
  addFavorite,
  removeFavorite,
  setFocusedClock,
}) => {
  const actions: any = [];
  if (clock) {
    if (clock.isInstalled && clock.hasSettings) {
      actions.push({
        id: 'settings-action',
        text: i18n('page.ClockGallery.MyClocks.details.settings')(),
        textColor: '#112530',
        arrow: true,
        onClick: () => {clock.navigateToSettings();}
      });
    }

    actions.push({
      id: 'all-details-action',
      text: i18n('page.ClockGallery.MyClocks.details.appDetailsButton')(),
      textColor: '#112530',
      arrow: true,
      to: `/v2/clock/${clock.id}`
    });
  }

  const {
    component: {
      Ratings: { rateClockface }
    }
  } = translations;

  // don't remove clock if there is only one available or
  // if it's selected for install or active when there are more available
  const canRemoveClock = (maxFavoriteClocks > 1 && favoriteClocksCount > 1) && !(clock?.selected || clock?.active);
  const showRemoveButton = favoriteClocksEnabled && !clock?.isInstalling && canRemoveClock;

  return (
    <PortalContainer show={show} backgroundColor="#ffffff">
      {!!clock && (
        <React.Fragment>
          <HeaderContainer>
            <AppName>
              {clock.name}
            </AppName>
            <StyledIcon
              id='close-clock-details'
              aria-label='close-clock-details'
              url={closeIcon}
              width={18}
              height={18}
              onClick={()=>{setFocusedClock(null);}}
            ><AutomationText/></StyledIcon>
          </HeaderContainer>
          <DevName>
            {clock.developer.name}
          </DevName>
          <ScrollableContainer>
            <AppScreenshotCarousel
              app={clock}
              deviceName={deviceName}
            />
            <SelectButton app={clock} userHasGalleryGrant={userHasGalleryGrant} />
            {clock.isPremium && <PremiumLabel />}
            <RatingContainer>
              <RatingTransition show={clock.showRatingInputV2}>
                <SectionTitle>{rateClockface()}</SectionTitle>
                <BasicContentBlock
                  borderTop
                  style={{ paddingTop: '14px', paddingBottom: '14px' }}
                >
                  <ConnectedRatingInput app={clock} />
                </BasicContentBlock>
              </RatingTransition>
            </RatingContainer>
            <StyledActionsContainer>
              {actions.map((action: any, index: number) => (
                <LinkAction
                  key={action.id}
                  {...action}
                  last={index === actions.length - 1 &&
                    (!favoriteClocksEnabled || clock.isInstalling)}
                />
              ))}
              {showRemoveButton && (
                <ManageFavoriteAction
                  clock={clock}
                  favoriteClocksCount={favoriteClocksCount}
                  maxFavoriteClocks={maxFavoriteClocks}
                  addLoading={addLoading}
                  removeLoading={removeLoading}
                  addFavorite={addFavorite}
                  removeFavorite={removeFavorite}
                />
              )}
            </StyledActionsContainer>
          </ScrollableContainer>
        </React.Fragment>
      )}
    </PortalContainer>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(ClockDetails);
